import React, { useEffect } from 'react';

import { Button, Container } from '@mui/material';
import { observer } from 'mobx-react';

import classes from './NgzCoachNotes.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import {
  EmptyList,
  LbPagination,
  PageTitle,
} from '../../../shared/components/ui-components';
import { NoteItem } from './NoteItem/NoteItem';
import { notesStore } from '../../stores/notesStore';
import { openModal } from '../../../shared/utils/reactUtils/openModal/openModal';
import { UpsertNoteModal } from './UpsertNoteModal/UpsertNoteModal';
import { useUsersList } from './hooks/useUsersList';
import { Note, UsersListOption } from '../../interfaces/note';
import {
  getDefaultValuesForAddNote,
  getDefaultValuesForEditNote,
} from './utils/getDefaultValues';

type NgzCoachNotesProps = { isSubTab?: boolean } & (
  | {
      customerId: undefined;
      customerFirstName: undefined;
      customerLastName: undefined;
      customerEmail: undefined;
    }
  | {
      /** when customerId exist it means that we are on customer profile tab */
      customerId: string;
      customerFirstName: string;
      customerLastName: string;
      customerEmail: string;
    }
);

const NgzCoachNotes = observer(
  ({
    isSubTab,
    customerId,
    customerFirstName,
    customerLastName,
    customerEmail,
  }: NgzCoachNotesProps) => {
    const { notes, getNotes, setDefaultValues, deleteNote, filterMethods } = notesStore;

    const userOption: UsersListOption | undefined = customerId
      ? {
          id: +customerId,
          name: `${customerFirstName} ${customerLastName}`,
          email: customerEmail,
          firstName: customerFirstName,
          lastName: customerLastName,
          img: null,
        }
      : undefined;

    const { usersList } = useUsersList({ userOption });

    const { isShowPlaceholder, elementsPerPage, totalElements, getPaginationProps } =
      filterMethods;

    useEffect(() => {
      getNotes(customerId);

      return () => {
        setDefaultValues();
      };
    }, []);

    const handleAddNote = async () => {
      openModal((closeModal) => (
        <UpsertNoteModal
          usersList={usersList}
          defaultValues={getDefaultValuesForAddNote(customerId, usersList)}
          onClose={closeModal}
        />
      ));
    };

    const handleEditNote = (note: Note) => {
      openModal((closeModal) => (
        <UpsertNoteModal
          noteId={note.id}
          usersList={usersList}
          defaultValues={getDefaultValuesForEditNote(note, usersList)}
          onClose={closeModal}
        />
      ));
    };

    return (
      <Container className={classes.container}>
        <PageTitle title="Notes" customTopMargin={isSubTab}>
          <Button
            className={classes.addNoteButton}
            variant="contained"
            size="medium"
            onClick={handleAddNote}
          >
            + Add a New Note
          </Button>
        </PageTitle>
        <div className={classes.notesList}>
          {notes.map((note) => (
            <NoteItem
              key={note.id}
              note={note}
              onEdit={handleEditNote}
              onDelete={deleteNote}
            />
          ))}
          <LbPagination
            variant="outBox"
            {...getPaginationProps()}
            elementsPerPage={elementsPerPage}
            totalElements={totalElements}
          />
        </div>
        <EmptyList show={isShowPlaceholder} description="There are no notes" />
      </Container>
    );
  },
);

angularizeWithProviders(NgzCoachNotes, 'ngzCoachNotes', {
  isSubTab: '<',
  customerId: '<',
  customerFirstName: '<',
  customerLastName: '<',
  customerEmail: '<',
});
